import { createConversation, listConversations } from '@/lib/chatBridge';

// ============================================================================
// chatFavorites.ts — «Избранное» (заметки себе, как Saved Messages).
//
// Механизм (по контракту сервера, БЕЗ новых RPC):
//   • direct-чат с самим собой сервер запрещает (нужен ровно один собеседник);
//   • группа с ОДНИМ участником (создатель) разрешена — это «чат себе»;
//   • сервер идемпотентен по составу участников: повторное создание группы
//     «только я» возвращает существующий чат — избранное не дублируется.
// Поэтому избранное — обычный групповой чат с единственным участником
// (текущий пользователь): лента, отправка заметок и вложений, пересылка —
// штатными механизмами (ChatThread / forwardMessage). SSoT — сервер; клиент
// лишь резолвит или создаёт чат.
// ============================================================================

/** Название чата избранного (подпись закреплённой строки в списке чатов). */
export const FAVORITES_CHAT_TITLE = 'Избранное';

/** Минимальный вид диалога для резолва избранного (подмножество Conv). */
export interface FavoriteConversation {
  id: string;
  kind?: string;
  title?: string;
  memberIds?: string[];
  contextType?: string;
}

/** Чат «только я»: группа без контекста с единственным участником — мной. */
export function isSelfChat(conv: FavoriteConversation, myId: string): boolean {
  return conv.kind === 'group'
    && !conv.contextType
    && Array.isArray(conv.memberIds)
    && conv.memberIds.length === 1
    && conv.memberIds[0] === myId;
}

/** Чат избранного среди диалогов пользователя: сначала чат с названием
 *  «Избранное», затем любой чат «только я» (второй такой создать нельзя —
 *  серверная идемпотентность по составу участников). */
export function findFavoritesChat(convs: FavoriteConversation[], myId: string): FavoriteConversation | null {
  const selfChats = convs.filter((c) => isSelfChat(c, myId));
  const named = selfChats.find((c) => c.title === FAVORITES_CHAT_TITLE);
  if (named) return named;
  const first = selfChats.length > 0 ? selfChats[0] : undefined;
  return first ?? null;
}

// Одна операция на приложение (single-flight): параллельные нажатия не создают
// гонки; идемпотентность сервера — второй уровень защиты.
let inflight: Promise<string> | null = null;
let inflightOwner = '';

/** Получить id чата избранного, создав его при первом обращении. */
export function ensureFavoritesChat(myId: string): Promise<string> {
  if (!myId) return Promise.reject(new Error('Не удалось определить текущего пользователя'));
  if (inflight && inflightOwner === myId) return inflight;
  inflightOwner = myId;
  inflight = (async () => {
    try {
      const items: FavoriteConversation[] = await listConversations();
      const found = findFavoritesChat(items, myId);
      if (found) return found.id;
      const created = await createConversation({ kind: 'group', title: FAVORITES_CHAT_TITLE, memberIds: [] });
      return created.id;
    } finally {
      inflight = null;
      inflightOwner = '';
    }
  })();
  return inflight;
}
